// ============================================
// LINEAPRECIOEDITABLE.JS — primitivos de edicion de precio/margen por linea
// ============================================
// Extraidos de Previsualizador.js (el precotizador) para reusarlos SIN duplicar en
// otras pantallas (ej. Colecciones). El comportamiento es EXACTAMENTE el mismo que
// tenia el precotizador; solo se movio el codigo a un modulo compartido.
//
// Exporta:
//   round2(n)                     -> redondeo a 2 decimales
//   colorMargen(pct)              -> color por umbral del margen (verde/naranja/rojo)
//   margenDePrecioCosto(p, c)     -> margen % semilla = (p - c)/p*100, null si falta dato
//   parsearPrecio(texto)          -> "3,724.58" / "3724,58" / "$3724.58" -> 3724.58 (o null)
//   formatoPrecio(n)              -> "$3,724.58" ("$1,947" si no hay centavos)
//   <InputPrecio valorMXN onCommit style />   input de precio en MXN (miles, decimales, editable)
//   <CampoPrecio value onChange />            igual, para formularios (contrato de <input>)
//   <InputMargen valor costo onChange style /> input de margen % (coloreado, enlazado)

import React, { useRef, useState } from 'react';
import { tokens } from '../styles/tokens';
import { COLOR_GRIS_CLARO } from './helpersClientes';
import { fmtDinero } from '../utils/dinero';

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Color del margen por umbral (igual que en la tienda): verde sano / naranja medio / rojo bajo.
export function colorMargen(pct) {
  if (pct == null) return COLOR_GRIS_CLARO;
  if (pct >= 25) return tokens.success;
  if (pct >= 15) return tokens.warning;
  return tokens.danger;
}

// Margen semilla derivado del precio/costo: (precio - costo) / precio * 100.
// null si no hay costo o precio (la linea muestra "—" y se edita el precio directo).
export function margenDePrecioCosto(precio, costo) {
  const p = Number(precio) || 0;
  const c = Number(costo) || 0;
  if (c <= 0 || p <= 0) return null;
  return round2(((p - c) / p) * 100);
}

// PRECIO-DECIMALES (2026-10-12): FUENTE UNICA para convertir lo que alguien teclea o PEGA en
// un precio. Antes cada campo filtraba con /[^0-9]/ y BORRABA el punto: "3724.58" se volvia
// 372458 (cien veces mas) y "3,724.58" pegado tambien. Reglas:
//   - se ignora todo lo que no sea digito, punto o coma ($, espacios, "MXN"...)
//   - con punto Y coma, el ULTIMO de los dos es el decimal: 3,724.58 · 3.724,58 -> 3724.58
//   - un solo separador repetido es de miles: 1,234,567 · 1.234.567 -> 1234567
//   - una sola coma seguida de EXACTAMENTE 3 digitos es de miles (3,724 -> 3724); con 1-2
//     digitos es decimal (3724,58 -> 3724.58, el teclado de Android en espanol pone coma)
//   - un solo punto es SIEMPRE decimal (en Mexico el punto es el decimal)
//   - se redondea a 2 decimales. Vacio o sin digitos -> null.
export function parsearPrecio(entrada) {
  const s = String(entrada == null ? '' : entrada).replace(/[^\d.,]/g, '');
  if (!/\d/.test(s)) return null;
  const iPunto = s.lastIndexOf('.');
  const iComa = s.lastIndexOf(',');
  let entero = s;
  let dec = '';
  if (iPunto >= 0 && iComa >= 0) {
    const sep = Math.max(iPunto, iComa);
    entero = s.slice(0, sep);
    dec = s.slice(sep + 1);
  } else if (iPunto >= 0 || iComa >= 0) {
    const ch = iComa >= 0 ? ',' : '.';
    const partes = s.split(ch);
    if (partes.length > 2) {
      entero = partes.join('');
    } else if (ch === ',' && partes[1].length === 3) {
      entero = partes[0] + partes[1];
    } else {
      entero = partes[0];
      dec = partes[1];
    }
  }
  entero = entero.replace(/[.,]/g, '');
  dec = dec.replace(/[.,]/g, '');
  const n = Number((entero || '0') + (dec ? '.' + dec : ''));
  return Number.isFinite(n) ? round2(n) : null;
}

// "$3,724.58" / "$1,947.00": SIEMPRE con centavos (SIN-REDONDEO 2026-10-12: antes un precio redondo
// se veia "$1,947" y en la misma ficha convivian dos formatos).
export function formatoPrecio(n) {
  return fmtDinero(round2(n));
}

// Lo que se ensena al enfocar para editar: sin "$" ni comas de miles, con punto decimal.
const textoEditable = (n) => String(round2(n));

// Input de precio en MXN: muestra "$3,724.58" (miles y centavos si los hay); al enfocar deja
// teclear o pegar con decimales (parsearPrecio); al cambiar llama onCommit con el numero
// (mantiene el enlace con margen); al perder foco reformatea. inputMode="decimal" para que el
// celular saque el teclado numerico CON punto/coma (el "numeric" no lo trae).
export function InputPrecio({ valorMXN, onCommit, style }) {
  const [foco, setFoco] = useState(false);
  const [texto, setTexto] = useState('');
  // Texto mostrado al enfocar. Solo se hace onCommit si el usuario realmente cambia
  // ese valor. Evita la deriva por redondeo en modo Con IVA: enfocar y salir (o
  // reescribir el MISMO numero mostrado) no debe mover el precio interno unos centavos.
  const textoFocoRef = useRef('');
  const numero = round2(valorMXN);
  const display = foco ? texto : formatoPrecio(numero);
  return (
    <input
      type="text" inputMode="decimal" autoComplete="off" aria-label="Precio de venta"
      value={display}
      onFocus={(e) => {
        setFoco(true);
        const t0 = textoEditable(numero);
        textoFocoRef.current = t0;
        setTexto(t0);
        const t = e.target; setTimeout(() => { try { t.select(); } catch {} }, 0);
      }}
      onChange={(e) => {
        const limpio = (e.target.value || '').replace(/[^\d.,]/g, '');
        setTexto(limpio);
        // Solo commit si cambio respecto a lo mostrado al enfocar (sin deriva).
        if (limpio !== textoFocoRef.current) onCommit(parsearPrecio(limpio) ?? 0);
      }}
      onBlur={() => setFoco(false)}
      style={style}
    />
  );
}

// Reemplazo directo de <input type="number" step="0.01"> en los FORMULARIOS de precio (Mis
// Productos): mismo contrato value/onChange(e) para no tocar la logica de cada formulario, pero
// acepta pegar "3,724.58" (el type=number lo rechazaba y dejaba el campo VACIO = precio 0) y la
// coma decimal del teclado del celular. Entrega en e.target.value el numero normalizado
// ("3724.58") o '' si se vacio.
export function CampoPrecio({ value, onChange, style, placeholder, 'aria-label': ariaLabel }) {
  const [foco, setFoco] = useState(false);
  const [texto, setTexto] = useState('');
  const visible = value == null ? '' : String(value);
  return (
    <input
      type="text" inputMode="decimal" autoComplete="off" aria-label={ariaLabel}
      placeholder={placeholder}
      value={foco ? texto : visible}
      onFocus={() => { setFoco(true); setTexto(visible); }}
      onChange={(e) => {
        const limpio = (e.target.value || '').replace(/[^\d.,]/g, '');
        setTexto(limpio);
        const n = parsearPrecio(limpio);
        onChange({ target: { value: n == null ? '' : String(n) } });
      }}
      onBlur={() => setFoco(false)}
      style={style}
    />
  );
}

// Input de margen: entero con sufijo "%", coloreado por umbral (Math.round); enlazado.
// Si no hay costo muestra "—". Mientras se edita conserva lo tecleado; pasa digitos a onChange.
export function InputMargen({ valor, costo, onChange, style }) {
  const [foco, setFoco] = useState(false);
  const [texto, setTexto] = useState('');
  if ((Number(costo) || 0) <= 0) {
    return <span style={{ color: COLOR_GRIS_CLARO }}>—</span>;
  }
  const m = (valor == null || valor === '') ? null : Math.round(Number(valor) || 0);
  const color = colorMargen(m);
  const display = foco ? texto : (m == null ? '' : String(m));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <input
        type="text" inputMode="numeric" aria-label="Margen porcentual"
        value={display}
        onFocus={(e) => { setFoco(true); setTexto(m == null ? '' : String(m)); const t = e.target; setTimeout(() => { try { t.select(); } catch {} }, 0); }}
        onChange={(e) => { const limpio = (e.target.value || '').replace(/[^0-9]/g, ''); setTexto(limpio); onChange(limpio); }}
        onBlur={() => setFoco(false)}
        style={{ ...style, color, fontWeight: 700 }}
      />
      <span style={{ color, fontWeight: 700, fontSize: 13 }}>%</span>
    </span>
  );
}
